import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack
} from '@mui/material';
import {
  CheckCircleOutline,
  PlayCircleOutline,
  VisibilityOutlined,
  InsightsOutlined,
  SchoolOutlined,
  FitnessCenterOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import TacticalStackVisual from './components/profundidade';

const LandingPage = () => {

  // Cores personalizadas para o tema
  const colors = {
    dark: '#07111f',
    darkLighter: '#0d1a2d',
    accent: '#10b981', // Verde Esmeralda Moderno
    textLight: '#f8fafc',
    textMuted: '#94a3b8'
  };
  const accent = {
    primary: '#10b981',
    glow: 'rgba(16,185,129,0.18)',
    border: 'rgba(16,185,129,0.26)',
    halo: 'rgba(16,185,129,0.16)',
    accentSoft: 'rgba(16,185,129,0.10)',
  };

  return (
    <Box sx={{ bgcolor: colors.dark, color: colors.textLight, minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>

      {/* 🔴 1. HERO SECTION (refeito) */}
      <Box
        sx={{
          minHeight: { xs: 'auto', sm: '92vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: colors.dark,
          py: { xs: 10, sm: 0 },
          backgroundImage: `
            radial-gradient(circle at 18% 20%, ${accent.halo} 0%, transparent 36%),
            radial-gradient(circle at 84% 26%, rgba(255,255,255,0.08) 0%, transparent 22%),
            radial-gradient(circle at 74% 72%, ${accent.accentSoft} 0%, transparent 30%),
            radial-gradient(circle at 58% 48%, rgba(255,255,255,0.03) 0%, transparent 18%),
            linear-gradient(145deg, ${colors.dark} 0%, ${colors.darkLighter} 54%, #13233b 100%)
          `,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.03) 46%, transparent 47%),
              linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px),
              linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px)
            `,
            backgroundSize: 'auto, 72px 72px, 72px 72px',
            backgroundPosition: '0 0, center center, center center',
            opacity: 0.22,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            '&::after': {
              content: '""',
              position: 'absolute',
              width: { xs: 260, md: 500 },
              height: { xs: 140, md: 220 },
              left: { xs: '-40px', md: '4%' },
              bottom: { xs: '18px', md: '40px' },
              borderRadius: '999px',
              border: `1px solid ${accent.border}`,
              transform: 'rotate(-10deg)',
              opacity: 0.3,
            },
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: { xs: '-20px', md: '34%' },
            top: { xs: '70px', md: '84px' },
            width: { xs: 180, md: 270 },
            height: 2,
            background: `linear-gradient(90deg, transparent 0%, ${accent.primary} 24%, ${accent.primary} 78%, transparent 100%)`,
            opacity: 0.48,
            transform: 'rotate(-24deg)',
            boxShadow: `0 0 18px ${accent.glow}`,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: { xs: '44px', md: '39%' },
            top: { xs: '42px', md: '58px' },
            width: { xs: 9, md: 12 },
            height: { xs: 9, md: 12 },
            borderRadius: '50%',
            bgcolor: accent.primary,
            boxShadow: `0 0 14px ${accent.primary}`,
            opacity: 0.92,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: { xs: '132px', md: '52%' },
            top: { xs: '136px', md: '168px' },
            width: { xs: 10, md: 14 },
            height: { xs: 10, md: 14 },
            borderRadius: '50%',
            bgcolor: accent.primary,
            boxShadow: `0 0 14px ${accent.primary}`,
            opacity: 0.92,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: { xs: '88px', md: '45%' },
            top: { xs: '92px', md: '108px' },
            width: { xs: 60, md: 96 },
            height: { xs: 60, md: 96 },
            borderTop: `2px dashed ${accent.border}`,
            borderRight: `2px dashed ${accent.border}`,
            borderRadius: '0 100px 0 0',
            opacity: 0.7,
            transform: 'rotate(-20deg)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: { xs: '56px', md: '37%' },
            bottom: { xs: '46px', md: '62px' },
            width: { xs: 220, md: 360 },
            height: 2,
            background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 15%, ${accent.primary} 48%, transparent 100%)`,
            opacity: 0.34,
            transform: 'rotate(12deg)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            right: { xs: '-18px', md: '7%' },
            top: { xs: '104px', md: '42px' },
            width: { xs: 260, md: 420 },
            height: { xs: 364, md: 588 },
            transform: 'rotate(11deg)',
            transformOrigin: 'center center',
            opacity: 0.24,
            pointerEvents: 'none',
            zIndex: 0,
            filter: `drop-shadow(0 0 22px ${accent.glow})`,
            WebkitMaskImage: 'radial-gradient(circle at center, rgba(0,0,0,1) 38%, rgba(0,0,0,0.72) 62%, rgba(0,0,0,0.18) 82%, transparent 100%)',
            maskImage: 'radial-gradient(circle at center, rgba(0,0,0,1) 38%, rgba(0,0,0,0.72) 62%, rgba(0,0,0,0.18) 82%, transparent 100%)',
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 100 140"
            preserveAspectRatio="xMidYMid meet"
            sx={{
              width: '100%',
              height: '100%',
              display: 'block',
              overflow: 'visible',
            }}
          >
            <defs>
              <linearGradient id="landingPitchStroke" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(16,185,129,0.06)" />
                <stop offset="45%" stopColor="rgba(16,185,129,0.18)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.08)" />
              </linearGradient>
            </defs>
            <rect x="2" y="2" width="96" height="136" fill="none" stroke="url(#landingPitchStroke)" strokeWidth="0.9" />
            <line x1="50" y1="2" x2="50" y2="138" stroke="url(#landingPitchStroke)" strokeWidth="0.9" />
            <circle cx="50" cy="70" r="15" fill="none" stroke="url(#landingPitchStroke)" strokeWidth="0.9" />
            <line x1="2" y1="70" x2="98" y2="70" stroke="rgba(16,185,129,0.20)" strokeWidth="0.95" />
            <rect x="25" y="2" width="50" height="20" fill="none" stroke="url(#landingPitchStroke)" strokeWidth="0.9" />
            <rect x="25" y="118" width="50" height="20" fill="none" stroke="url(#landingPitchStroke)" strokeWidth="0.9" />
          </Box>
        </Box>

        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent 0%, ${accent.primary} 40%, rgba(255,255,255,0.55) 52%, transparent 100%)`,
          zIndex: 1,
        }} />

        {/* vinheta */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            boxShadow: 'inset 0 0 140px rgba(0,0,0,0.72)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        {/* fade para a próxima seção */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 140,
            background: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, ${colors.dark} 100%)`,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6}>
            {/* Texto (esquerda) */}
            <Grid item xs={12}>
              <Stack spacing={3} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.accent,
                    fontWeight: 800,
                    letterSpacing: 2.5,
                    opacity: 0.95,
                  }}
                >
                  LEITURA DE JOGO NÃO É TALENTO. É MÉTODO.
                </Typography>

                {/* H1 = transformação */}
                <Typography
                  variant="h1"
                  component="h1"
                >
                  Cada jogo deixa pistas. <br />
                  Quem tem o mapa, antecipa.
                </Typography>

                {/* “Formação em...” vira subtítulo */}
                <Typography
                  variant="h5"
                  sx={{
                    color: colors.textLight,
                    fontWeight: 600,
                    opacity: 0.92,
                    letterSpacing: -0.2,
                  }}
                >
                  Formação em Análise Tática e Leitura de Jogo
                </Typography>

                {/* promessa operacional (maior que body, menor que H1) */}
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.textMuted,
                    fontWeight: 400,
                    lineHeight: 1.65,
                  }}
                >
                  Assista à aula <strong>O Mapa do Futebol</strong> gratuitamente. <br />
                  Depois disso, você nunca mais lê o jogo da mesma forma.
                </Typography>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{
                    pt: 2,
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayCircleOutline />}
                    sx={{
                      bgcolor: colors.accent,
                      color: colors.dark,
                      fontWeight: 800,
                      px: 4.5,
                      py: 1.7,
                      fontSize: '1.05rem',
                      borderRadius: 999,
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#14c58b', transform: 'translateY(-1px)' },
                      transition: 'all 0.25s',
                    }}
                    href="/mapa-futebol"
                  >
                    O Mapa do Futebol (Aula Grátis)
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<InfoOutlined />}
                    sx={{
                      borderColor: 'rgba(148,163,184,0.35)',
                      color: colors.textLight,
                      fontWeight: 700,
                      px: 4.5,
                      py: 1.7,
                      fontSize: '1.05rem',
                      borderRadius: 999,
                      textTransform: 'none',
                      '&:hover': { borderColor: 'rgba(148,163,184,0.65)', bgcolor: 'rgba(148,163,184,0.06)' },
                    }}
                    href="/formacao"
                  >
                    Conheça a Formação Completa
                  </Button>
                </Stack>

              </Stack>
            </Grid>
          </Grid>
        </Container>

        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent 0%, ${accent.primary} 40%, rgba(255,255,255,0.55) 52%, transparent 100%)`,
          zIndex: 1,
        }} />
      </Box>

      {/* 🔴 CONTEÚDO GRÁTIS */}
      <Box
        id="conteudo-gratuito"
        sx={{
          py: { xs: 12, md: 16 },
          bgcolor: '#F8FAFC', // fundo claro neutro elegante
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">

            {/* TEXTO */}
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>

                <Typography
                  variant="overline"
                  sx={{
                    color: colors.accent,
                    fontWeight: 800,
                    letterSpacing: 2.2,
                  }}
                >
                  PRIMEIRA ETAPA
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.3rem', md: '2.7rem' },
                    lineHeight: 1.1,
                    letterSpacing: -0.8,
                    color: colors.dark,
                  }}
                >
                  O Mapa do Futebol é o primeiro passo.
                </Typography>

                <Typography
                  sx={{
                    color: '#475569', // cinza mais neutro
                    lineHeight: 1.75,
                    fontSize: '1.05rem',
                    maxWidth: 520,
                  }}
                >
                  Na primeira aula, você passa a:
                </Typography>

                {/* bullets */}
                <Stack spacing={1.2} sx={{ mt: 1 }}>
                  {[
                    'Ver a organização do jogo com clareza',
                    'Reconhecer padrões recorrentes',
                    'Entender o que antecede o lance',
                    'Ler o jogo com mais segurança',
                  ].map((t) => (
                    <Box key={t} sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
                      <CheckCircleOutline sx={{ color: colors.accent }} />
                      <Typography sx={{ color: colors.dark }}>
                        {t}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayCircleOutline />}
                  sx={{
                    bgcolor: colors.accent,
                    color: colors.dark,
                    fontWeight: 800,
                    px: 4.5,
                    py: 1.7,
                    borderRadius: 999,
                    textTransform: 'none',
                    mt: 2,
                    boxShadow: '0 10px 30px rgba(16,185,129,0.25)',
                    '&:hover': { bgcolor: '#14c58b' },
                  }}
                  href="/mapa-futebol"
                >
                  Começar pelo Mapa
                </Button>

              </Stack>
            </Grid>

            {/* PREVIEW */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  //borderRadius: 4,
                  height: { xs: 240, md: 320 },
                  //backgroundColor: '#0F172A', // base escura para destacar o frame
                  //border: '1px solid rgba(15,23,42,0.15)',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Placeholder da imagem real */}
                <img
                  src="images/frame-mapa-futebol.png"
                  alt="Trecho da aula O Mapa do Futebol"
                  style={{
                    //width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>

              <Typography
                sx={{
                  mt: 1,
                  fontSize: '0.8rem',
                  color: '#64748B',
                  textAlign: 'center',
                }}
              >
                Frame da aula “O Mapa do Futebol”
              </Typography>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* 🔷 PROFUNDIDADE - EXPLODED VIEW (perspectiva real) */}
      <Box
        sx={{
          py: { xs: 12, md: 18 },
          bgcolor: colors.dark,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* textura sutil */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
        radial-gradient(900px 420px at 18% 28%, rgba(16,185,129,0.10), transparent 60%),
        radial-gradient(700px 360px at 78% 55%, rgba(148,163,184,0.08), transparent 62%)
      `,
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">

            {/* TEXTO */}
            <Grid item xs={12} md={5}>
              <Stack spacing={2.5}>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.3rem', md: '2.8rem' },
                    lineHeight: 1.08,
                    letterSpacing: -0.9,
                  }}
                >
                  O Mapa é apenas a superfície.
                </Typography>

                <Typography
                  sx={{
                    color: colors.textMuted,
                    fontSize: '1.05rem',
                    lineHeight: 1.7,
                    maxWidth: 520,
                  }}
                >
                  O mapa organiza o campo.
                  A formação ensina a enxergar a estrutura que sustenta o jogo.
                </Typography>

                <Box
                  sx={{
                    width: 64,
                    height: 6,
                    borderRadius: 999,
                    bgcolor: 'rgba(16,185,129,0.9)',
                    display: 'flex',
                  }}
                />

                <Typography sx={{ color: colors.textLight }}>
                  Por trás de cada lance existe organização, relações e padrões que não aparecem na superfície.
                  É preciso um olhar treinado para perceber as múltiplas camadas do jogo.
                </Typography>

              </Stack>
            </Grid>

            {/* VISUAL */}
            <Grid item xs={12} md={7}>
              <TacticalStackVisual />
            </Grid>
          </Grid>
        </Container>
      </Box>


      {/* 🔴 SLOGAN / POSICIONAMENTO */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          bgcolor: '#f4f4f4',
        }}
      >
        <Container>
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography
              variant="overline"
              sx={{
                color: colors.dark,
                fontWeight: 800,
                letterSpacing: 2.5,
                opacity: 0.95,
              }}
            >
              ANÁLISE + LEITURA, EM UMA SÓ FORMAÇÃO
            </Typography>

            <Grid container spacing={4} justifyContent="center">
              {/* ========= BLOCO 1: ANÁLISE TÁTICA EXPLICA ========= */}
              <Grid item xs={12} md={6} >
                <Typography
                  component="h2"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.4rem', md: '3rem' },
                    lineHeight: 1.15,
                    letterSpacing: -1.2,
                    color: '#0F172A',
                    maxWidth: 980,
                    mb:2,
                  }}
                >
                  Análise Tática<br/>Explica.
                </Typography>

                <Grid container spacing={2}>
                  {/* 01 */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative', p: 4, height: '100%', textAlign: 'center' }}>
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: -20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '5.5rem',
                          fontWeight: 900,
                          color: 'rgba(15,23,42,0.05)',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                      >
                        01
                      </Typography>

                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: 'rgba(71, 71, 71, 0.12)',
                          color: 'rgba(100, 100, 100, 0.95)',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <SchoolOutlined />
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.35rem',
                          color: '#0F172A',
                          mb: 1,
                        }}
                      >
                        Aprenda
                      </Typography>

                      <Typography
                        sx={{
                          color: '#475569',
                          lineHeight: 1.75,
                          fontSize: '1.02rem',
                        }}
                      >
                        Domine os conceitos que estruturam o jogo. Modelos, cenários, funções, padrões e situações reais.
                      </Typography>
                    </Box>
                  </Grid>

                  {/* 02 */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative', p: 4, height: '100%' }}>
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: -20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '5.5rem',
                          fontWeight: 900,
                          color: 'rgba(15,23,42,0.05)',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                      >
                        02
                      </Typography>

                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: 'rgba(71, 71, 71, 0.12)',
                          color: 'rgba(100, 100, 100, 0.95)',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <InsightsOutlined />
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.35rem',
                          color: '#0F172A',
                          mb: 1,
                        }}
                      >
                        Explique
                      </Typography>

                      <Typography
                        sx={{
                          color: '#475569',
                          lineHeight: 1.75,
                          fontSize: '1.02rem',
                        }}
                      >
                        Deixe de apenas descrever o lance. Saiba explicar causas e por que ele aconteceu.
                      </Typography>
                    </Box>

                  </Grid>
                </Grid>
              </Grid>

              {/* ========= BLOCO 2: LEITURA DE JOGO ANTECIPA ========= */}
              <Grid item xs={12} md={6} >

                <Typography
                  component="h2"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.4rem', md: '3rem' },
                    lineHeight: 1.15,
                    letterSpacing: -1.2,
                    color: 'rgba(16,185,129,0.95)',
                    maxWidth: 980,
                    mb:2,
                  }}
                >
                  Leitura de Jogo<br/>Antecipa.
                </Typography>

                <Grid container spacing={2}>
                  {/* 03 */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative', p: 4, height: '100%' }}>
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: -20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '5.5rem',
                          fontWeight: 900,
                          color: 'rgba(15,23,42,0.05)',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                      >
                        03
                      </Typography>

                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: 'rgba(16,185,129,0.12)',
                          color: 'rgba(16,185,129,0.95)',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <FitnessCenterOutlined />
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.35rem',
                          color: 'rgba(16,185,129,0.95)',
                          mb: 1,
                        }}
                      >
                        Pratique
                      </Typography>

                      <Typography
                        sx={{
                          color: '#475569',
                          lineHeight: 1.75,
                          fontSize: '1.02rem',
                        }}
                      >
                        Treine o olhar com exercícios e validações. Transforme teoria em leitura ativa.
                      </Typography>
                    </Box>
                  </Grid>

                  {/* 04 */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative', p: 4, height: '100%' }}>
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: -20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '5.5rem',
                          fontWeight: 900,
                          color: 'rgba(15,23,42,0.05)',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                      >
                        04
                      </Typography>

                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: 'rgba(16,185,129,0.12)',
                          color: 'rgba(16,185,129,0.95)',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <VisibilityOutlined />
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.35rem',
                          color: 'rgba(16,185,129,0.95)',
                          mb: 1,
                        }}
                      >
                        Antecipe
                      </Typography>

                      <Typography
                        sx={{
                          color: '#475569',
                          lineHeight: 1.75,
                          fontSize: '1.02rem',
                        }}
                      >
                        Os desdobramentos deixam de ser aleatórios. Você passa a antecipar as tendências do jogo.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Box
              sx={{
                width: 64,
                height: 6,
                borderRadius: 999,
                bgcolor: 'rgba(16,185,129,0.9)',
                display: 'flex',
              }}
            />

            <Typography
              sx={{
                color: '#475569',
                fontSize: '1.5rem',
                lineHeight: 1.6,
                maxWidth: 820,
              }}
            >
              A formação integra análise tática e leitura de jogo em um único método.
              Você aprende a explicar o jogo - e treina para antecipá-lo.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* 🔴 PARA QUEM É */}
      <Box
        sx={{
          py: { xs: 12, md: 16 },
          bgcolor: '#e7e7e7',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            {/* título + suporte */}
            <Grid item xs={12} md={5}>
              <Stack spacing={2.2}>
                <Typography
                  component="h2"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.2rem', md: '2.7rem' },
                    lineHeight: 1.12,
                    letterSpacing: -0.8,
                    color: '#0F172A',
                  }}
                >
                  Para quem leva o jogo a sério.
                </Typography>

                <Typography sx={{ color: '#475569', lineHeight: 1.85, fontSize: '1.08rem' }}>
                  Não é sobre “gostar de futebol”. É sobre decidir enxergar o jogo com mais clareza.
                </Typography>
              </Stack>
            </Grid>

            {/* lista */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  pl: { xs: 2.2, md: 3 },
                  borderLeft: '6px solid rgba(16,185,129,0.9)',
                }}
              >
                <Stack spacing={2.2}>
                  {[
                    'Profissionais que buscam evolução constante.',
                    'Criadores que desejam ir além do óbvio.',
                    'Quem busca argumentos sólidos, não opinião.',
                    'Quem sente que existe uma camada invisível do jogo.',
                    'Quem não se contenta com um olhar superficial.',
                  ].map((text) => (
                    <Typography
                      key={text}
                      sx={{
                        fontSize: '1.12rem',
                        lineHeight: 1.9,
                        color: '#334155',
                      }}
                    >
                      {text}
                    </Typography>
                  ))}

                  <Typography
                    sx={{
                      fontSize: '1.15rem',
                      fontWeight: 700,
                      color: '#0F172A',
                      mt: 1,
                    }}
                  >
                    Se você se reconhece aqui, está no lugar certo.
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 12, md: 16 },
          background: 'linear-gradient(135deg, #0B1220 0%, #0F1C2E 100%)',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={5} alignItems="center">

            {/* Eyebrow */}
            <Typography
              sx={{
                color: colors.accent,
                fontWeight: 800,
                letterSpacing: 3,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                opacity: 0.9,
              }}
            >
              FAÇA PARTE DA FORMAÇÃO
            </Typography>

            {/* Headline */}
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.4rem', md: '3.2rem' },
                lineHeight: 1.15,
                letterSpacing: -1,
              }}
            >
              Quem estuda o jogo, enxerga antes.
              Quem não estuda, reage depois.
            </Typography>

            {/* Subheadline */}
            <Typography
              sx={{
                fontSize: '1.15rem',
                color: 'rgba(255,255,255,0.75)',
                maxWidth: 800,
                lineHeight: 1.7,
              }}
            >
              Com o método certo, cada minuto de estudo vira capacidade real de leitura de jogo.
            </Typography>

            {/* Botões */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              sx={{ mt: 2 }}
            >

              <Button
                variant="contained"
                size="large"
                startIcon={<PlayCircleOutline />}
                sx={{
                  bgcolor: colors.accent,
                  color: colors.dark,
                  fontWeight: 800,
                  px: 4.5,
                  py: 1.7,
                  fontSize: '1.05rem',
                  borderRadius: 999,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#14c58b', transform: 'translateY(-1px)' },
                  transition: 'all 0.25s',
                }}
                href="/mapa-futebol"
              >
                O Mapa do Futebol (Aula Grátis)
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<InfoOutlined />}
                sx={{
                  borderColor: 'rgba(148,163,184,0.35)',
                  color: colors.textLight,
                  fontWeight: 700,
                  px: 4.5,
                  py: 1.7,
                  fontSize: '1.05rem',
                  borderRadius: 999,
                  textTransform: 'none',
                  '&:hover': { borderColor: 'rgba(148,163,184,0.65)', bgcolor: 'rgba(148,163,184,0.06)' },
                }}
                href="/formacao"
              >
                Conheça a Formação Completa
              </Button>
            </Stack>

          </Stack>
        </Container>
      </Box>


    </Box>
  );
};

export default LandingPage;
